import { useEffect, useRef } from 'react';
import apiClient from '../services/api';

/**
 * useSessionHeartbeat — v2
 *
 * Fix: builds absolute URLs from apiClient's baseURL so requests hit
 * the PHP backend (e.g. http://localhost:8000/api) instead of the CRA
 * dev server on port 3000.
 *
 * NOTE the import path above assumes this file lives at
 * src/components/hooks/ (two levels below src/). If you move it to
 * src/hooks/, change the import to '../services/api'.
 */

// e.g. "http://localhost:8000/api" in dev, "/api" or full URL in prod
const API_BASE = (apiClient?.defaults?.baseURL || '/api').replace(/\/$/, '');

export const useSessionHeartbeat = ({ onLogout, intervalMs = 60000 }) => {
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  useEffect(() => {
    let stopped = false;

    const beat = async () => {
      try {
        const res = await fetch(`${API_BASE}/heartbeat.php`, {
          method: 'POST',
          credentials: 'include',
        });
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          console.warn('⚠️ Heartbeat returned non-JSON — check API_BASE:', API_BASE);
          return;
        }
        const data = await res.json();
        if (!stopped && !data.success && data?.data?.session_replaced) {
          console.warn('⚠️ Session replaced by another device — logging out');
          onLogoutRef.current?.();
        } else if (!stopped && !data.success && res.status === 401) {
          onLogoutRef.current?.();
        }
      } catch {
        // Network hiccup — don't log the user out, just retry next tick
      }
    };

    beat();
    const timer = setInterval(beat, intervalMs);

    const handlePageHide = () => {
      try {
        navigator.sendBeacon(
          `${API_BASE}/release-session.php`,
          new Blob([], { type: 'application/json' })
        );
      } catch {
        /* sendBeacon unsupported — stale timeout covers it */
      }
    };
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [intervalMs]);
};

export default useSessionHeartbeat;